import { css } from '@emotion/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useReducer, useState } from 'react';
import {
  AtlasRowCoverageInput,
  AtlasRowSourceSelect,
  AtlasUploadOptionsValue,
  defaultAtlasUploadOptions,
  isAtlasUploadOptionsValid,
} from './AtlasUploadOptionsFields';
import { FileUploadEntry, fileUploadReducer, FileUploadStatus } from './multiFileUploadState';
import { formatFileSize, parseFundFromFilename } from './utils/parseFundFromFilename';
import { UploadRoute, useUploadMultipleGeneralLedgers } from './uploadGeneralLedgerActions';

/**
 * Pick a sensible default route based on the selected files.
 *  - Every file matches the strict Carta pattern → Carta (operators batch-uploading
 *    Carta exports; the date comes from the filename and is required).
 *  - Otherwise → Atlas. SS&C operators rename files freely so we should not gate
 *    them on filename detection — the form-level route is the source of truth.
 */
function inferDefaultRoute(entries: FileUploadEntry[]): UploadRoute {
  if (entries.length === 0) {
    return 'atlas';
  }
  return entries.every((e) => e.source === 'carta') ? 'carta' : 'atlas';
}

function filesToEntries(files: File[]): FileUploadEntry[] {
  return files
    .filter((f) => /\.(csv|xlsx?|xls)$/i.test(f.name))
    .map((file) => {
      const parsed = parseFundFromFilename(file.name);
      return {
        file,
        source: parsed.source,
        detectedFundName: parsed.fundName,
        detectedDate: parsed.date,
        parseError: parsed.error,
        status: 'pending' as FileUploadStatus,
        errorMessage: null,
        // Each row owns its own Atlas opts. localStorage-backed default lets
        // newly added rows inherit the operator's last picked source.
        atlasOpts: defaultAtlasUploadOptions(),
      };
    });
}

interface MultiFileUploadFormProps {
  initialFiles?: File[];
  onClose: () => void;
  onBack?: () => void;
}

export function MultiFileUploadForm({ initialFiles, onClose, onBack }: MultiFileUploadFormProps) {
  const [entries, dispatch] = useReducer(fileUploadReducer, [], () =>
    initialFiles ? filesToEntries(initialFiles) : []
  );
  const [isUploading, setIsUploading] = useState(false);
  // route: which upload pipeline to use for every file in the batch. Defaults
  // to inferDefaultRoute (Carta if all files match the strict Carta pattern,
  // else Atlas) on first render. Operator can flip explicitly — filename is
  // not the source of truth for SS&C/Atlas.
  const [route, setRoute] = useState<UploadRoute>(() => inferDefaultRoute(entries));
  const uploadMultiple = useUploadMultipleGeneralLedgers();

  const hasFiles = entries.length > 0;
  const isAtlasRoute = route === 'atlas';
  const allDone = hasFiles && entries.every((e) => e.status === 'success' || e.status === 'error');
  const successCount = entries.filter((e) => e.status === 'success').length;
  const errorCount = entries.filter((e) => e.status === 'error').length;
  // Carta requires a YYYY-MM-DD date in the filename. Block the upload (and
  // surface a per-row warning) when the operator picks Carta but any file
  // lacks a detected date — otherwise the request would fail server-side and
  // the only feedback would be a generic toast.
  const hasInvalidCartaEntries = !isAtlasRoute && entries.some((e) => !e.detectedDate);
  // Atlas requires every row to have a valid sourceObject + (optional)
  // well-formed coverage range. The "+ New" sentinel without a typed name
  // fails the schema's required-string check, which is what we want.
  const hasInvalidAtlasEntries = useMemo(
    () => isAtlasRoute && entries.some((e) => !isAtlasUploadOptionsValid(e.atlasOpts)),
    [isAtlasRoute, entries]
  );

  // Status column is pure noise pre-upload — every row is 'pending'. Reveal it
  // only once an upload is in flight or a per-file result exists, so the
  // resting layout doesn't pay for a column that says the same thing in every row.
  const showStatusColumn = isUploading || entries.some((e) => e.status === 'success' || e.status === 'error');

  const handleRemove = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_FILE', index });
  }, []);

  const handleAtlasOptsChange = useCallback((index: number, next: AtlasUploadOptionsValue) => {
    dispatch({ type: 'SET_ATLAS_OPTS', index, atlasOpts: next });
  }, []);

  const handleUploadAll = useCallback(async () => {
    setIsUploading(true);
    // Per-file opts in entries order. The hook receives one AtlasUploadOpts
    // per row; on the carta path it's ignored.
    const atlasOptsByIndex = entries.map((e) => ({
      sourceObject: e.atlasOpts.sourceObject,
      ...(e.atlasOpts.coverageStart ? { coverageStart: e.atlasOpts.coverageStart } : {}),
      ...(e.atlasOpts.coverageEnd ? { coverageEnd: e.atlasOpts.coverageEnd } : {}),
    }));
    await uploadMultiple(entries, route, atlasOptsByIndex, (index, status, errorMessage) => {
      dispatch({ type: 'SET_STATUS', index, status, errorMessage });
    });
    setIsUploading(false);
  }, [entries, route, uploadMultiple]);

  if (!hasFiles) {
    return (
      <Box css={emptyState}>
        <Typography variant='body2' color='text.secondary'>
          No valid files selected.
        </Typography>
        {onBack && (
          <Button variant='text' onClick={onBack}>
            Back
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box css={dialogContent}>
      {!allDone && (
        <Box css={sectionBox}>
          <Typography variant='subtitle2' gutterBottom>
            Upload route
          </Typography>
          <Typography variant='caption' color='text.secondary' css={sectionHelp}>
            Carta uses the per-file pipeline (date extracted from the filename). SS&C / other FAs go through
            the new pipeline where filename doesn't matter. Default is chosen from your files; flip if you're
            uploading a different type.
          </Typography>
          <RadioGroup
            row
            value={route}
            onChange={(e) => setRoute(e.target.value as UploadRoute)}
            css={routeControl}
          >
            <FormControlLabel
              value='carta'
              control={<Radio size='small' />}
              label='Carta'
              disabled={isUploading}
            />
            <FormControlLabel
              value='atlas'
              control={<Radio size='small' />}
              label='SS&C / other FAs'
              disabled={isUploading}
            />
          </RadioGroup>
          {!isAtlasRoute && (
            <Typography variant='caption' color='text.secondary' css={sectionHelp}>
              Expected filename: <code>&lt;fund-slug&gt;_YYYY-MM-DD_gl-export.csv</code> or <code>.xlsx</code>{' '}
              — the fund and period are read from the name (e.g.{' '}
              <code>acme-fund-lp_2026-03-26_gl-export.xlsx</code>).
            </Typography>
          )}
        </Box>
      )}
      <TableContainer css={tableContainer}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell css={headerCell}>File</TableCell>
              {isAtlasRoute ? (
                <>
                  <TableCell css={[headerCell, atlasSourceCell, sectionHeaderCell]}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <span>Source object</span>
                      <Tooltip
                        arrow
                        title='Picks which template rules apply to the file. Defaults to "generalLedger" for general-ledger uploads. Choose an existing source from the FA template, or declare a new one with "+ New".'
                      >
                        <InfoOutlinedIcon fontSize='small' css={sectionInfoIconCss} />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell css={[headerCell, atlasCoverageCell, sectionHeaderCell]}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <span>Coverage (optional)</span>
                      <Tooltip
                        arrow
                        title="Optional. Declares the period this file covers. If omitted, the system infers the period from the data's GL dates. Useful when the file contains intentional period boundaries (e.g. a quarterly trial balance) or when inference might miss the intended scope."
                      >
                        <InfoOutlinedIcon fontSize='small' css={sectionInfoIconCss} />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell css={headerCell}>Detected Fund</TableCell>
                  <TableCell css={headerCell}>Date</TableCell>
                </>
              )}
              <TableCell css={[headerCell, sizeCell]} align='right'>
                Size
              </TableCell>
              {showStatusColumn && (
                <TableCell css={[headerCell, statusHeaderCell]} align='left' width={80}>
                  Status
                </TableCell>
              )}
              <TableCell css={headerCell} width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <TableRow key={`${entry.file.name}-${entry.file.size}-${index}`} css={bodyRow}>
                <TableCell css={fileNameCell}>
                  <Tooltip title={entry.file.name} placement='top-start'>
                    <Typography variant='body2' css={fileNameText}>
                      {entry.file.name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {isAtlasRoute ? (
                  <>
                    <TableCell css={atlasSourceCell}>
                      <AtlasRowSourceSelect
                        value={entry.atlasOpts.sourceObject}
                        onChange={(next) =>
                          handleAtlasOptsChange(index, { ...entry.atlasOpts, sourceObject: next })
                        }
                        disabled={isUploading}
                      />
                    </TableCell>
                    <TableCell css={atlasCoverageCell}>
                      <Stack direction='row' spacing={0.5} alignItems='flex-start'>
                        <AtlasRowCoverageInput
                          value={entry.atlasOpts.coverageStart}
                          onChange={(next) =>
                            handleAtlasOptsChange(index, { ...entry.atlasOpts, coverageStart: next })
                          }
                          pair={entry.atlasOpts}
                          field='coverageStart'
                          disabled={isUploading}
                        />
                        <AtlasRowCoverageInput
                          value={entry.atlasOpts.coverageEnd}
                          onChange={(next) =>
                            handleAtlasOptsChange(index, { ...entry.atlasOpts, coverageEnd: next })
                          }
                          pair={entry.atlasOpts}
                          field='coverageEnd'
                          disabled={isUploading}
                        />
                      </Stack>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      {entry.detectedFundName ? (
                        <Typography variant='body2'>{entry.detectedFundName}</Typography>
                      ) : (
                        <Stack direction='row' alignItems='center' spacing={0.5}>
                          <WarningAmberIcon fontSize='small' color='warning' />
                          <Typography variant='caption' color='warning.main'>
                            {entry.parseError || 'Unknown fund'}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.detectedDate ? (
                        <Typography variant='body2'>{entry.detectedDate}</Typography>
                      ) : (
                        <Stack direction='row' alignItems='center' spacing={0.5}>
                          <WarningAmberIcon fontSize='small' color='warning' />
                          <Typography variant='caption' color='warning.main'>
                            No date in filename
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                  </>
                )}
                <TableCell align='right' css={sizeCell}>
                  <Typography variant='body2' color='text.secondary' css={sizeText}>
                    {formatFileSize(entry.file.size)}
                  </Typography>
                </TableCell>
                {showStatusColumn && (
                  <TableCell align='left' css={statusCell} width={80}>
                    <StatusIndicator status={entry.status} errorMessage={entry.errorMessage} />
                  </TableCell>
                )}
                <TableCell>
                  {entry.status === 'pending' && !isUploading && (
                    <IconButton size='small' onClick={() => handleRemove(index)}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary when done */}
      {allDone && (
        <Typography variant='body2' css={summaryText}>
          {errorCount === 0
            ? `All ${successCount} files uploaded successfully.`
            : `${successCount} of ${entries.length} files uploaded. ${errorCount} failed.`}
        </Typography>
      )}

      {/* Actions */}
      <Stack direction='row' justifyContent='flex-end' spacing={1} css={actions}>
        {!isUploading && !allDone && onBack && (
          <Button variant='text' onClick={onBack}>
            Back
          </Button>
        )}
        <Button variant='text' color='primary' onClick={onClose}>
          {allDone ? 'Close' : 'Cancel'}
        </Button>
        {!allDone && (
          <Button
            variant='contained'
            onClick={handleUploadAll}
            disabled={isUploading || entries.length === 0 || hasInvalidCartaEntries || hasInvalidAtlasEntries}
          >
            {isUploading ? 'Uploading...' : `Upload ${entries.length} File${entries.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function StatusIndicator({
  status,
  errorMessage,
}: {
  status: FileUploadStatus;
  errorMessage: string | null;
}) {
  switch (status) {
    case 'pending':
      return null;
    case 'uploading':
      return <CircularProgress size={18} />;
    case 'processing':
      // Uploaded to S3; the loader is running server-side. Spinner + tooltip so the
      // operator knows it's accepted but not yet done — success is reported only once
      // the backend finishes.
      return (
        <Tooltip title='Uploaded — processing on the server' placement='top-start'>
          <CircularProgress size={18} />
        </Tooltip>
      );
    case 'success':
      return <CheckCircleIcon fontSize='small' color='success' />;
    case 'error':
      // Surface the backend's failure reason inline (clamped to two lines) so
      // operators don't have to hover to see what went wrong. The full message
      // stays in the tooltip — backend errors (e.g. the coverage-mode unbounded
      // notice) can run multiple sentences.
      return (
        <Tooltip title={errorMessage || 'Upload failed'} placement='top-start'>
          <Stack direction='row' spacing={0.75} alignItems='flex-start' css={errorRow}>
            <ErrorOutlineIcon fontSize='small' color='error' css={errorIcon} />
            <Typography variant='caption' color='error.main' css={errorText}>
              {errorMessage || 'Upload failed'}
            </Typography>
          </Stack>
        </Tooltip>
      );
  }
}

// Match BasicDialog's DialogTitle paddingX (2.5rem) so the form's left edge
// lines up with "Upload General Ledger" — the dialog itself sets DialogContent
// to padding:0, so the form provides its own.
const dialogContent = css`
  padding: 1rem 2.5rem 1.5rem;
`;

const emptyState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
`;

const tableContainer = css`
  max-height: 400px;
  overflow-y: auto;
`;

const headerCell = css`
  font-weight: 600;
  white-space: nowrap;
`;

const fileNameCell = css`
  max-width: 240px;
`;

/* Truncate long filenames with an ellipsis; the row Tooltip surfaces the full
 * name on hover so nothing is hidden. */
const fileNameText = css`
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Subtle hover so operators can tell which row's inputs they're editing
 * without competing with the input focus states. */
const bodyRow = css`
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

/* Source + Coverage sit next to each other on the Atlas route. Default MUI
 * TableCell horizontal padding (16px) on adjacent cells stacks into a ~32px
 * gap that visually disconnects them. Trim the inner edges so the controls
 * read as one logical group. */
const atlasSourceCell = css`
  padding-right: 4px;
`;

const atlasCoverageCell = css`
  padding-left: 4px;
`;

const sectionBox = css`
  padding: 8px 0 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const sectionHelp = css`
  display: block;
  margin-bottom: 8px;
`;

const routeControl = css`
  margin-top: 4px;
`;

const summaryText = css`
  margin-top: 12px;
  text-align: center;
`;

const actions = css`
  margin-top: 16px;
  padding: 8px 0 16px;
`;

/* "Source object" / "Coverage (optional)" header cells: caption-weight,
 * uppercase column-group label so they read as section markers rather than
 * competing with the input labels below. */
const sectionHeaderCell = css`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 600;
  padding-bottom: 4px;
`;

/* Info icon paired with the caption-style section headers — match the
 * secondary-text color so the icon doesn't louden a deliberately quiet label. */
const sectionInfoIconCss = css`
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
`;

/* Size cell: nowrap + right-aligned + tabular-nums so values like
 * '360.73 MB' don't wrap onto two lines and digits stay column-aligned. */
const sizeCell = css`
  white-space: nowrap;
  text-align: right;
`;

const sizeText = css`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/* Lock the Status header to the same width as the body cell so the column
 * doesn't grow when long inline errors render. */
const statusHeaderCell = css`
  width: 80px;
  flex-shrink: 0;
`;

/* Fix the Status cell at ~80px so the column doesn't grow when long inline
 * errors render. The error text (already 2-line clamped) wraps within. */
const statusCell = css`
  width: 80px;
  flex-shrink: 0;
`;

const errorRow = css`
  /* Keep the icon top-aligned with the first line of multi-line text. */
  padding-top: 2px;
`;

const errorIcon = css`
  flex-shrink: 0;
  margin-top: 1px;
`;

/* Clamp the inline reason to two lines so a multi-sentence backend message
 * doesn't blow up the row height. Tooltip carries the full text. */
const errorText = css`
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;
